import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import { FieldTypeLink } from '@allesauto/utils/storyblok';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React from 'react';

import './BenefitTeaser.css';

import Benefit, { BenefitProps } from './benefit/Benefit';
import { BenefitsTeaserAdapter } from './BenefitTeaserAdapter';
import TextLink from '../textLink';

export { BenefitsTeaserAdapter };

interface BenefitTeaser {
  headline?: string;
  subHeadline?: string;
  isMultiColor?: boolean;
  isNumeric?: boolean;
  textLinkText?: string;
  textLinkHref?: FieldTypeLink;
  numberOfColumns?: number;
}
export interface BenefitsTeaserProps {
  benefitTeaser: BenefitTeaser;
  benefits?: BenefitProps[];
  background?: 'WHITE' | 'GREY';
  component: string;
  _uid: string;
  _editable?: string;
}

const BenefitsTeaser: React.FC<BenefitsTeaserProps> = (props) => {
  const { benefitTeaser, benefits, background = 'WHITE' } = props;

  const breakpoint = useScreenBreakpoint();

  const url =
    benefitTeaser.textLinkHref &&
    getUrlFromStoryblokLink(benefitTeaser.textLinkHref);
  const benefitLink = (url && parseSlashToURL(url)) || '';
  const className = {
    wrapper: clsx(
      'sm:pt-40px sm:px-15px lg:pt-100px lg:px-60px',
      !benefitTeaser.isMultiColor && 'bg-white',
      background === 'GREY' ? 'bg-grey10' : 'bg-white'
    ),
    headlineContent: clsx(
      'flex sm:flex-col lg:flex-row ',
      benefitTeaser.subHeadline
        ? 'sm:mb-30px lg:mb-60px'
        : 'sm:mb-20px lg:mb-40px'
    ),
    headlineAndSubHeadline: clsx('flex flex-col w-full'),
    headlineAndTextLink:
      'align-middle flex sm:flex-col lg:flex-row lg:justify-between',
    headline: 'font-headline font-weight-bold text-grey100 text-headline2',
    subHeadline:
      'font-weight-normal !font-regular break-words text-p1 grid lg:grid-cols-2 mt-20px',
    benefitList: clsx(
      'grid',
      benefitTeaser.numberOfColumns === 4
        ? 'sm:gap-x-0 lg:grid-cols-4'
        : benefitTeaser.numberOfColumns === 3
        ? 'sm:gap-x-0 lg:grid-cols-3'
        : 'sm:gap-x-0 lg:grid-cols-2'
    ),
    textLink: clsx(
      background === 'GREY' && 'benefit-link-grey',
      'sm:mt-20px lg:mt-0'
    ),
  };

  return (
    <div {...sbEditable(props)} className={className.wrapper}>
      {benefitTeaser.headline && (
        <div className={className.headlineContent}>
          <div className={className.headlineAndSubHeadline}>
            <div className={className.headlineAndTextLink}>
              <h3 className={className.headline}>{benefitTeaser.headline}</h3>
              {breakpoint >= SCREEN_BREAKPOINT.LG &&
                benefitTeaser.textLinkText && (
                  <TextLink
                    text={benefitTeaser.textLinkText}
                    href={benefitLink}
                    customClassName={className.textLink}
                    target={benefitTeaser?.textLinkHref?.target ?? '_self'}
                  />
                )}
            </div>
            {benefitTeaser.subHeadline && (
              <h4 className={className.subHeadline}>
                {benefitTeaser.subHeadline}
              </h4>
            )}
          </div>
          {breakpoint < SCREEN_BREAKPOINT.LG && benefitTeaser.textLinkText && (
            <TextLink
              text={benefitTeaser.textLinkText}
              href={benefitLink}
              customClassName={className.textLink}
            />
          )}
        </div>
      )}
      <div className={className.benefitList}>
        {benefits &&
          benefits.map((benefit, idx) => (
            <Benefit
              key={`benefit-${idx}`}
              {...benefit}
              isMultiColor={benefitTeaser.isMultiColor}
              isNumeric={benefitTeaser.isNumeric}
              numberOfColumns={benefitTeaser.numberOfColumns}
            />
          ))}
      </div>
    </div>
  );
};

export default BenefitsTeaser;
